import { 
  YStack, 
  XStack, 
  ScrollView, 
  Text, 
  H2, 
  H3, 
  Paragraph, 
  Card, 
  Separator, 
  Theme, 
  SizableText,
  Progress
} from '@my/ui';
import { 
  PieChart, 
  TrendingUp, 
  ShieldAlert, 
  CheckCircle2, 
  Target, 
  Activity 
} from '@tamagui/lucide-icons-2';

// Assuming the JSON is imported or defined here
import { ZAInvestmentPortfolioDefinitions2026 } from '../ZAInvestmentPortfolioMarkdown'; // Update path as needed
import { color } from '@my/util';

type PortfolioProps = {
  portfolioName: string;
};

export const PortfolioDetailView = ({ portfolioName }: PortfolioProps) => {
  const portfolio = ZAInvestmentPortfolioDefinitions2026.portfolios[portfolioName];
  const definitions = ZAInvestmentPortfolioDefinitions2026.holding_definitions;

  if (!portfolio) {
    return (
      <YStack f={1} alignItems="center" justifyContent="center" p="$4">
        <H3 color="$red10">Portfolio Not Found</H3>
        <Paragraph>Could not load data for: {portfolioName}</Paragraph>
      </YStack>
    );
  }

  return (
      <ScrollView f={1} backgroundColor="$background" overflow='visible'>
        <YStack gap="$5" paddingBottom="$8">
          
          {/* --- Header Section --- */}
          {/* <YStack gap="$2">
            <H2 size="$9" fontWeight="800" color="$color">
              {portfolio.portfolio_name}
            </H2>
            <Paragraph size="$4" color="$gray11" lineHeight="$5">
              {portfolio.overview}
            </Paragraph>
          </YStack> */}

          <Separator />

          {/* --- Key Metrics / Philosophy --- */}
          <XStack flexWrap="wrap" gap="$3">
            <InfoCard 
              icon={<Target size={20} color="$blue10" />} 
              title="Philosophy" 
              content={portfolio.investment_philosophy} 
            />
             <InfoCard 
              icon={<Activity size={20} color="$green10" />} 
              title="Investment Thesis" 
              content={portfolio.investment_thesis} 
            />
          </XStack>

          {/* --- Holdings Section --- */}
          <YStack gap="$4">
            <XStack alignItems="center" gap="$2">
              <PieChart size={24} />
              <H3>Holdings & Allocation</H3>
            </XStack>
            
            <YStack gap="$3">
              {portfolio.holdings.map((holding) => {
                const def = definitions[holding.ticker];
                const cleanPercent = parseInt(holding.allocation.replace('%', ''));
                
                return (
                  <Card key={holding.ticker} elevate size="$4" bordered padding={18} background={'white'}>
                    <YStack gap="$2">
                      <XStack justifyContent="space-between" alignItems="center">
                        <YStack style={{flexShrink: 1}}>
                          <H3 fontSize="$6">{holding.ticker}</H3>
                          <SizableText size="$2" color="black">{def?.fund_name}</SizableText>
                        </YStack>
                        <YStack alignItems="flex-end">
                           <H2 color={color.tzaGreen}>{holding.allocation}</H2>
                        </YStack>
                      </XStack>

                      {/* Visual Progress Bar for Allocation */}
                      <Progress value={cleanPercent} max={100} size="$1">
                        <Progress.Indicator transition="bouncy" backgroundColor={color.tzaGreen} />
                      </Progress>

                      <Paragraph size="$3" color="black" mt="$2">
                        {def?.description}
                      </Paragraph>
                    </YStack>
                  </Card>
                );
              })}
            </YStack>
          </YStack>

          {/* --- Bullet Lists (Ideal For & Risks) --- */}
          <YStack md={{ flexDirection: 'row' }} gap="$4">
            
            {/* Ideal For Column */}
            <Card f={1} gap="$3" elevate size="$4" bordered padding={18} background={'white'}>
              <XStack alignItems="center" gap="$2">
                <CheckCircle2 size={20} color="black" />
                <H3 fontSize="$5" color="black">Ideal For</H3>
              </XStack>
              <YStack gap="$2">
                {portfolio.ideal_for.map((item, i) => (
                  <BulletPoint key={i} text={item} />
                ))}
              </YStack>
            </Card>

            {/* Risks Column */}
            <Card f={1} gap="$3" elevate size="$4" bordered padding={18} background={'white'}>
              <XStack alignItems="center" gap="$2">
                <ShieldAlert size={20} color="black" />
                <H3 fontSize="$5" color="black">Risk Considerations</H3>
              </XStack>
              <YStack gap="$2">
                {portfolio.risk_considerations.map((item, i) => (
                  <BulletPoint key={i} text={item} />
                ))}
              </YStack>
            </Card>

          </YStack>

          {/* --- Expected Behavior --- */}
          <YStack gap="$3" mt="$2">
            <XStack alignItems="center" gap="$2">
              <TrendingUp size={24} />
              <H3>Expected Behavior</H3>
            </XStack>
            <Card f={1} gap="$3" elevate size="$4" bordered padding={18} background={'white'}>
              <YStack gap="$2">
                {portfolio.expected_behavior.map((item, i) => (
                  <BulletPoint key={i} text={item} bulletColor="black" />
                   /* <XStack key={i} gap="$2">
                     <Text color="$gray8">•</Text>
                     <Paragraph size="$3">{item}</Paragraph>
                   </XStack> */
                ))}
              </YStack>
            </Card>
          </YStack>

        </YStack>
      </ScrollView>
  );
};

// --- Sub Components ---

const InfoCard = ({ icon, title, content }: { icon: any, title: string, content: string }) => (
  <Card style={{width: '100%'}} gap="$3" elevate size="$4" bordered padding={18} background={'white'}>
    <XStack alignItems="center" gap="$2" mb="$1">
      {icon}
      <H3 fontSize="$5" color="black">{title}</H3>
      {/* <SizableText fontWeight="700" size="$4">{title}</SizableText> */}
    </XStack>
    <Paragraph size="$3" color="black">
      {content}
    </Paragraph>
  </Card>
);

const BulletPoint = ({ text, bulletColor = "black" }: { text: string, bulletColor?: string }) => (
  <XStack gap="$2" alignItems="center">
    <YStack pt="$1.5">
      <YStack width={6} height={6} borderRadius={10} backgroundColor={bulletColor} />
    </YStack>
    <Paragraph size="$3" lineHeight={undefined} flex={1}>
      {text}
    </Paragraph>
  </XStack>
);
